import addHours from "date-fns/addHours";
import getHours from "date-fns/getHours";
import getMinutes from "date-fns/getMinutes";
import isSaturday from "date-fns/isSaturday";
import isWeekend from "date-fns/isWeekend";
import startOfHour from "date-fns/startOfHour";
import _get from "lodash/get";
import moment from "moment";
import { MaybeEmpty } from "../constants";
import { ReflectSuccessType, ReflectType } from "../models";
import { LookedUpIndividual, StreetAddress } from "../models/external/ocmInterface";

export const formatBirthDate = (birthDate?: string | null | number): string | undefined => {
    if (!birthDate) return undefined;

    const dateObj = moment.utc(birthDate).utcOffset(120);
    if (!dateObj.isValid()) {
        // oxlint-disable-next-line no-console
        console.warn("Birthdate is not valid:", birthDate);
        return undefined;
    }

    return moment.utc(birthDate).utcOffset(120).format("YYYY-MM-DD");
};

export const isRunningOnClientSide = (): boolean => typeof window !== "undefined";

export const validateLookedUpIndividual = (
    lookedUpIndividual?: Omit<LookedUpIndividual, "street_address"> & { street_address?: StreetAddress },
    { allowMissingStreetAddress = false }: { allowMissingStreetAddress?: boolean } = {},
): boolean => {
    const hasFirstName = _get(lookedUpIndividual, "first_name", "").length > 0;
    const hasSurname = _get(lookedUpIndividual, "surname", "").length > 0;

    if (!hasFirstName || !hasSurname) {
        return false;
    }

    const streetAddress = _get(lookedUpIndividual, "street_address");
    if (allowMissingStreetAddress && !streetAddress) {
        return true;
    }
    const normalizedStreetAddress: MaybeEmpty<StreetAddress> = streetAddress || {};

    const countryCodeRegEx = /^[A-Z]{3,3}$/;
    const postcodeRegEx = /^[0-9]{4,4}$/;

    return (
        postcodeRegEx.test(normalizedStreetAddress.postcode || "") &&
        countryCodeRegEx.test(normalizedStreetAddress.country_code || "") &&
        _get(normalizedStreetAddress, "city", "").length > 0
    );
};

export const reflect = <T>(promise: Promise<T>, id?: string | number): Promise<ReflectType<T>> =>
    promise.then(
        (s) => ({ s, id, status: "fulfilled" }),
        (e) => ({ e, id, status: "rejected" }),
    );

export const fulfilledReflects = <T>(ref: ReflectType<T>[]): T[] =>
    (ref.filter((r) => r.status === "fulfilled") as ReflectSuccessType<T>[]).map(({ s }) => s);

export const checkLeadingAndTrailingSlash = (url: string): string => {
    if (!url.endsWith("/")) {
        url += "/";
    }

    if (!url.startsWith("/")) {
        url = "/" + url;
    }

    return url;
};

export const getEarliestPossiblePortingDate = (now = new Date()): Date => {
    let portingDate;
    const hasStartedHour = getMinutes(now) > 0;

    // Round to nearest hour
    portingDate = hasStartedHour ? addHours(startOfHour(now), 1) : now;

    // Add an extra business hour if we're late enough in the hour that the order may spill into the next hour before completion
    const requiredBusinessHours = getMinutes(now) >= 46 ? 6 : 5;

    // Business hours: Mon-Fri 08:00-20:00 and 08:00-16 on Saturdays
    let businessHoursPassed = 0;
    while (businessHoursPassed < requiredBusinessHours) {
        const isBusinessHour = !isWeekend(portingDate) && getHours(portingDate) >= 8 && getHours(portingDate) < 20;
        const isSaturdayBusinessHour =
            isSaturday(portingDate) && getHours(portingDate) >= 8 && getHours(portingDate) < 16;

        if (businessHoursPassed < requiredBusinessHours) {
            portingDate = addHours(portingDate, 1);
        }

        if (isBusinessHour || isSaturdayBusinessHour) {
            businessHoursPassed += 1;
        }
    }

    return portingDate;
};

export const getDefaultPortinDate = (isEsim?: boolean): string => {
    if (isEsim) {
        const esimPortingDate = moment.tz(getEarliestPossiblePortingDate(), "UTC");
        return esimPortingDate.format("YYYY-MM-DDTHH:mm:ss") + "Z";
    }

    const today = moment.tz(new Date(), "UTC");
    const defaultPortinDate = today.add(7, "day");

    return defaultPortinDate.format("YYYY-MM-DDTHH:mm:ss") + "Z";
};

export const removeWhitespace = (str: string) => str.replace(/\s/g, "");

// Vouchers are on the format: G-N1DBW8-CTF7PH-BWK0T0-331
export const isVoucher = (code: string) => code.at(1) === "-";

// Unique smart discounts are one the format: RK-ELTAKQ-LUNRTY-830
export const isUniqueSmartDiscount = (code: string) => code.startsWith("RK-");
